'use client';

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import * as agentApi from '@/lib/agents/api';
import type { Agent, UpdateAgentInput, LLMProvider } from '@/lib/agents/types';
import { LLM_PROVIDER_INFO } from '@/lib/agents/types';
import { Loader2, Save, Trash2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

interface AgentSettingsProps {
  agent: Agent;
  onUpdate: (agent: Agent) => void;
  onDelete: () => void;
}

export function AgentSettings({ agent, onUpdate, onDelete }: AgentSettingsProps) {
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [name, setName] = useState(agent.name);
  const [description, setDescription] = useState(agent.description || '');
  const [isActive, setIsActive] = useState(agent.is_active);
  const [isPublic, setIsPublic] = useState(agent.is_public);
  const [systemPrompt, setSystemPrompt] = useState(agent.config.systemPrompt || '');
  const [llmProvider, setLlmProvider] = useState<LLMProvider>(agent.config.llmProvider);
  const [model, setModel] = useState(agent.config.model || '');
  const [temperature, setTemperature] = useState(agent.config.temperature ?? 0.7);
  const [maxIterations, setMaxIterations] = useState(agent.config.maxIterations || 10);

  const hasChanges =
    name !== agent.name ||
    description !== (agent.description || '') ||
    isActive !== agent.is_active ||
    isPublic !== agent.is_public ||
    systemPrompt !== (agent.config.systemPrompt || '') ||
    llmProvider !== agent.config.llmProvider ||
    model !== (agent.config.model || '') ||
    temperature !== (agent.config.temperature ?? 0.7) ||
    maxIterations !== (agent.config.maxIterations || 10);

  const handleReset = () => {
    setName(agent.name);
    setDescription(agent.description || '');
    setIsActive(agent.is_active);
    setIsPublic(agent.is_public);
    setSystemPrompt(agent.config.systemPrompt || '');
    setLlmProvider(agent.config.llmProvider);
    setModel(agent.config.model || '');
    setTemperature(agent.config.temperature ?? 0.7);
    setMaxIterations(agent.config.maxIterations || 10);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Agent name is required');
      return;
    }

    setSaving(true);
    try {
      const input: UpdateAgentInput = {
        name: name.trim(),
        description: description.trim() || undefined,
        isActive,
        isPublic,
        config: {
          systemPrompt: systemPrompt.trim() || undefined,
          llmProvider,
          model: model.trim() || undefined,
          temperature,
          maxIterations,
        },
      };

      const response = await agentApi.updateAgent(agent.id, input);
      toast.success('Agent updated successfully');
      onUpdate(response.data);
    } catch (error) {
      toast.error((error as Error).message || 'Failed to update agent');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await agentApi.deleteAgent(agent.id);
      toast.success('Agent deleted');
      onDelete();
    } catch (error) {
      toast.error((error as Error).message || 'Failed to delete agent');
    } finally {
      setDeleting(false);
    }
  };

  const providerModels = LLM_PROVIDER_INFO[llmProvider]?.models || [];

  return (
    <div className="space-y-6">
      {/* Basic Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Basic Information</CardTitle>
          <CardDescription>Update agent name and description</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Agent name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this agent do?"
              rows={2}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Active</Label>
              <p className="text-sm text-muted-foreground">Enable or disable this agent</p>
            </div>
            <Switch checked={isActive} onCheckedChange={setIsActive} />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Public</Label>
              <p className="text-sm text-muted-foreground">Allow others in your org to use this agent</p>
            </div>
            <Switch checked={isPublic} onCheckedChange={setIsPublic} />
          </div>
        </CardContent>
      </Card>

      {/* LLM Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">LLM Configuration</CardTitle>
          <CardDescription>Configure the language model settings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Provider</Label>
              <Select value={llmProvider} onValueChange={(v) => {
                setLlmProvider(v as LLMProvider);
                setModel(''); // Reset model when provider changes
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LLM_PROVIDER_INFO).map(([key, info]) => (
                    <SelectItem key={key} value={key}>
                      {info.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Model</Label>
              <Select value={model} onValueChange={setModel}>
                <SelectTrigger>
                  <SelectValue placeholder="Default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Default</SelectItem>
                  {providerModels.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Temperature: {temperature.toFixed(2)}</Label>
            </div>
            <Slider
              value={[temperature]}
              onValueChange={([v]) => setTemperature(v)}
              min={0}
              max={1}
              step={0.05}
            />
            <p className="text-xs text-muted-foreground">
              Lower = more focused, Higher = more creative
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="maxIterations">Max Iterations</Label>
            <Input
              id="maxIterations"
              type="number"
              value={maxIterations}
              onChange={(e) => setMaxIterations(parseInt(e.target.value) || 10)}
              min={1}
              max={50}
            />
            <p className="text-xs text-muted-foreground">
              Maximum tool call iterations before stopping
            </p>
          </div>
        </CardContent>
      </Card>

      {/* System Prompt */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">System Prompt</CardTitle>
          <CardDescription>Define the agent&apos;s behavior and personality</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            placeholder="You are a helpful assistant..."
            rows={6}
            className="font-mono text-sm"
          />
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" disabled={deleting}>
              {deleting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Delete Agent
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Agent</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete &quot;{agent.name}&quot; and all its execution history.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset} disabled={!hasChanges || saving}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Reset
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges || saving}>
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
